import { Suspense } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { AdjustmentDetailSidebar, CurrencyValue, FileList, LoaderOverlay } from 'components';
import { AdjustmentType, AdjustmentTypeText } from 'enums';
import { formatDate } from 'functions';
import { useBackupDocument } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import { usePaymentDocuments } from 'state';
import { PaymentDetail } from 'types';
import styles from '../PaymentDetailSidebar/PaymentDetailSidebar.module.scss';

type Props = {
    activeDetail?: PaymentDetail,
    disableDisputeButton?: boolean,
    expanded: boolean,
    selectedDetails: PaymentDetail[],
    setActiveSelectedDetail?: (value: PaymentDetail | undefined) => void,
    setExpanded: (value: boolean) => void,
    setSelectedDetails: (value: PaymentDetail[]) => void,
};

export default function PaymentMultiDetailSidebar({
    activeDetail,
    disableDisputeButton,
    expanded,
    selectedDetails,
    setActiveSelectedDetail,
    setExpanded,
    setSelectedDetails
}: Props) {
    const selectedDetail = activeDetail && selectedDetails.some(detail => detail.rowNumber === activeDetail.rowNumber)
        ? activeDetail
        : selectedDetails[0];

    if (!selectedDetail) {
        return null;
    }

    const setSelectedDetailsAndActive = (details: PaymentDetail[]) => {
        setSelectedDetails(details);
        if (!details.length) {
            setActiveSelectedDetail && setActiveSelectedDetail(undefined);
        }
    };

    const removeSelectedDetail = (detail: PaymentDetail) => {
        const nextDetails = selectedDetails.filter(selected => selected.rowNumber !== detail.rowNumber);
        setSelectedDetailsAndActive(nextDetails);

        if (!nextDetails.length) {
            setExpanded(false);
            return;
        }

        if (selectedDetail.rowNumber === detail.rowNumber) {
            setActiveSelectedDetail && setActiveSelectedDetail(nextDetails[0]);
        }
    };

    return (
        <AdjustmentDetailSidebar
            disableDisputeButton={disableDisputeButton}
            expanded={expanded}
            selectedDetail={selectedDetail}
            selectedDetailsForDispute={selectedDetails}
            setExpanded={setExpanded}
            setSelectedDetails={setSelectedDetailsAndActive}
        >
            <div className={styles.multiSummary}>
                <div className={styles.multiCount}>{selectedDetails.length} selected</div>
            </div>

            <div className={styles.multiList}>
                {selectedDetails.map(detail => (
                    <PaymentMultiDetailCard
                        detail={detail}
                        key={detail.rowNumber}
                        onRemove={() => removeSelectedDetail(detail)}
                    />
                ))}
            </div>
        </AdjustmentDetailSidebar>
    );
};

type PaymentMultiDetailCardProps = {
    detail: PaymentDetail,
    onRemove: () => void
};

const PaymentMultiDetailCard = ({ detail, onRemove }: PaymentMultiDetailCardProps) => (
    <div className={styles.multiCard}>
        <div className={styles.multiCardHeader}>
            <span>{getDetailTitle(detail)}</span>
            <div className={styles.multiCardActions}>
                <button
                    aria-label={`Remove ${getRemoveLabel(detail)}`}
                    className={styles.multiRemoveButton}
                    onClick={onRemove}
                    title={`Remove ${getRemoveLabel(detail)}`}
                    type="button"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
        </div>
        <div className={styles.multiCardDetails}>
            {getDetailItems(detail).map(item => (
                <DetailItem
                    key={item.name}
                    name={item.name}
                    value={item.value}
                />
            ))}
        </div>
        <div className={styles.multiDocuments}>
            <div className={styles.header}>
                <span className={styles.text}>Backup Documentation</span>
            </div>
            <Suspense fallback={<LoaderOverlay isLoading container height="3rem" />}>
                <Documents detail={detail} />
            </Suspense>
        </div>
    </div>
);

type ElementProps = {
    name: string,
    value: any
};

const DetailItem = ({ name, value }: ElementProps) => (
    <div className={styles.item}>
        <div className={styles.name}>{name}</div>
        <div className={styles.value}>{value}</div>
    </div>
);

const Documents = ({ detail }: { detail: PaymentDetail }) => {
    const documents = usePaymentDocuments(detail);
    const getDocument = useBackupDocument();

    return documents.length === 0
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};

const getDetailTitle = (detail: PaymentDetail) => {
    return AdjustmentTypeText[detail.adjustmentTypeId] ?? `Line ${detail.rowNumber}`;
};

const getRemoveLabel = (detail: PaymentDetail) => {
    switch (detail.adjustmentTypeId) {
        case AdjustmentType.CustomerDeduction:
            return `Deduction ${formatDisplayValue(detail.invoiceId)}`;
        case AdjustmentType.CustomerDedRepay:
            return `Repay ${formatDisplayValue(detail.invoiceId)}`;
        case AdjustmentType.DotBillback:
            return `Voucher ${formatDisplayValue(detail.voucherId)}`;
        case AdjustmentType.DotCreditToSupplier:
            return `Voucher ${formatDisplayValue(detail.voucherId)}`;
        case AdjustmentType.Discount:
            return `Invoice ${formatDisplayValue(detail.invoiceId)}`;
        default:
            return `Line ${detail.rowNumber}`;
    }
};

const getDetailItems = (detail: PaymentDetail) => {
    const amount = detail.adjustmentTypeId === AdjustmentType.Discount
        ? detail.discountPaymentAmount
        : detail.adjustmentAmount;

    const dateItem = { name: 'Date:', value: detail.invoiceDate ? formatDate(detail.invoiceDate) : 'Not Available' };
    const amountItem = { name: 'Amount:', value: <CurrencyValue value={amount} /> };

    if (detail.adjustmentTypeId === AdjustmentType.CustomerDeduction || detail.adjustmentTypeId === AdjustmentType.CustomerDedRepay) {
        return [
            dateItem,
            { name: 'Customer:', value: formatDisplayValue(detail.customerName) },
            amountItem,
            { name: 'Mock Invoice Number:', value: formatDisplayValue(detail.mockInvoice) },
            { name: 'Reference Number:', value: formatDisplayValue(detail.customerItemReference) }
        ];
    }

    if (detail.adjustmentTypeId === AdjustmentType.DotBillback || detail.adjustmentTypeId === AdjustmentType.DotCreditToSupplier) {
        const invoiceLabel = detail.adjustmentTypeId === AdjustmentType.DotBillback ? 'Billback Number:' : 'Credit Number:';
        const voucherLabel = detail.adjustmentTypeId === AdjustmentType.DotBillback ? 'Voucher:' : 'VoucherId:';

        return [
            { name: 'Mar Details:', value: formatDisplayValue(detail.marBillTypeDescription) },
            dateItem,
            amountItem,
            { name: invoiceLabel, value: formatDisplayValue(detail.invoiceId) },
            { name: voucherLabel, value: formatDisplayValue(detail.voucherId) }
        ];
    }

    if (detail.adjustmentTypeId === AdjustmentType.Discount) {
        return [
            { name: 'Mar Details:', value: formatDisplayValue(detail.marBillTypeDescription) },
            dateItem,
            amountItem
        ];
    }

    return [dateItem, amountItem];
};

const formatDisplayValue = (value: any) => {
    if (value === undefined || value === null || value === '') {
        return 'Not Available';
    }

    return value;
};
